import React, { useState, useEffect, useMemo } from 'react';
import { Trophy, Users, Medal, Target, ChevronDown, Trash2 } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Helper: Generate combinations of k elements from array
function getCombinations(arr, k) {
  const result = [];
  function combine(start, combo) {
    if (combo.length === k) {
      result.push([...combo]);
      return;
    }
    for (let i = start; i < arr.length; i++) {
      combo.push(arr[i]);
      combine(i + 1, combo);
      combo.pop();
    }
  }
  combine(0, []);
  return result;
}

export default function TrainingStats({ allUsers }) {
  const { user, role } = useAuth();
  const [trainingGames, setTrainingGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [groupView, setGroupView] = useState('einzel');
  const [sortBy, setSortBy] = useState('wins');
  const [deleting, setDeleting] = useState(false);
  const [deletingGameId, setDeletingGameId] = useState(null);
  const [showAllGames, setShowAllGames] = useState(false);
  const [groupStats, setGroupStats] = useState({
    einzel: [], duo: [], trio: [], quartett: [], five: []
  });

  // Toggle show all games
  const toggleShowAllGames = () => setShowAllGames(prev => !prev);

  // Delete a single game (Admin only)
  const deleteSingleGame = async (gameId) => {
    console.log('[TrainingStats] deleteSingleGame called', { gameId, user: user?.uid, role });
    
    if (!user) {
      alert('Bitte erst einloggen');
      return;
    }
    
    if (role !== 'admin') {
      alert('Nur Admins können Spiele löschen');
      return;
    }
    
    // Skip confirm dialog - delete directly
    setDeletingGameId(gameId);
    try {
      const apiUrl = `${BACKEND_URL}/api/training/games/${gameId}?admin_id=${user.uid}`;
      console.log('[TrainingStats] Calling DELETE:', apiUrl);
      
      const response = await fetch(apiUrl, {
        method: 'DELETE'
      });
      
      console.log('[TrainingStats] Response status:', response.status);
      const result = await response.json();
      console.log('[TrainingStats] Delete result:', result);
      
      if (result.ok) {
        // Remove from local state and recalculate stats
        const updatedGames = trainingGames.filter(g => g.id !== gameId);
        setTrainingGames(updatedGames);
        recalculateStats(updatedGames);
        console.log('[TrainingStats] Game deleted successfully, remaining:', updatedGames.length);
      } else {
        alert('Fehler beim Löschen: ' + (result.detail || 'Unbekannt'));
      }
    } catch (err) {
      console.error('[TrainingStats] Delete error:', err);
      alert('Fehler beim Löschen: ' + err.message);
    }
    setDeletingGameId(null);
  };

  // Recalculate stats after deletion
  const recalculateStats = (games) => {
    if (!games || games.length === 0) {
      setGroupStats({ einzel: [], duo: [], trio: [], quartett: [], five: [] });
      return;
    }
    
    const stats = {};
    const groupData = { duo: {}, trio: {}, quartett: {}, five: {} };
    
    games.forEach(game => {
      const winningTeam = game.winner === 'A' ? game.team_a_players : game.team_b_players;
      const losingTeam = game.winner === 'A' ? game.team_b_players : game.team_a_players;
      const winScore = game.winner === 'A' ? game.team_a_score : game.team_b_score;
      const loseScore = game.winner === 'A' ? game.team_b_score : game.team_a_score;
      
      // Individual stats
      winningTeam.forEach(playerId => {
        if (!stats[playerId]) stats[playerId] = { wins: 0, losses: 0, games: 0, pointsFor: 0, pointsAgainst: 0 };
        stats[playerId].wins++;
        stats[playerId].games++;
        stats[playerId].pointsFor += winScore;
        stats[playerId].pointsAgainst += loseScore;
      });
      
      losingTeam.forEach(playerId => {
        if (!stats[playerId]) stats[playerId] = { wins: 0, losses: 0, games: 0, pointsFor: 0, pointsAgainst: 0 };
        stats[playerId].losses++;
        stats[playerId].games++;
        stats[playerId].pointsFor += loseScore;
        stats[playerId].pointsAgainst += winScore;
      });
      
      // Group stats (duo, trio, etc.)
      [2, 3, 4, 5].forEach(size => {
        const groupKey = size === 2 ? 'duo' : size === 3 ? 'trio' : size === 4 ? 'quartett' : 'five';
        
        const winCombos = getCombinations(winningTeam, Math.min(size, winningTeam.length));
        const loseCombos = getCombinations(losingTeam, Math.min(size, losingTeam.length));
        
        winCombos.forEach(combo => {
          if (combo.length !== size) return;
          const key = [...combo].sort().join('_');
          if (!groupData[groupKey][key]) {
            groupData[groupKey][key] = { players: combo, wins: 0, losses: 0, pointsFor: 0, pointsAgainst: 0 };
          }
          groupData[groupKey][key].wins++;
          groupData[groupKey][key].pointsFor += winScore;
          groupData[groupKey][key].pointsAgainst += loseScore;
        });
        
        loseCombos.forEach(combo => {
          if (combo.length !== size) return;
          const key = [...combo].sort().join('_');
          if (!groupData[groupKey][key]) {
            groupData[groupKey][key] = { players: combo, wins: 0, losses: 0, pointsFor: 0, pointsAgainst: 0 };
          }
          groupData[groupKey][key].losses++;
          groupData[groupKey][key].pointsFor += loseScore;
          groupData[groupKey][key].pointsAgainst += winScore;
        });
      });
    });
    
    // Convert to arrays with calculated fields
    const einzelStats = Object.entries(stats).map(([playerId, s]) => ({
      players: [playerId],
      ...s,
      winRate: s.games > 0 ? ((s.wins / s.games) * 100).toFixed(0) : '0',
      diff: s.pointsFor - s.pointsAgainst
    }));
    
    const newGroupStats = { einzel: einzelStats };
    ['duo', 'trio', 'quartett', 'five'].forEach(key => {
      newGroupStats[key] = Object.values(groupData[key]).map(g => ({
        ...g,
        games: g.wins + g.losses,
        winRate: (g.wins + g.losses) > 0 ? ((g.wins / (g.wins + g.losses)) * 100).toFixed(0) : '0',
        diff: g.pointsFor - g.pointsAgainst
      }));
    });
    
    setGroupStats(newGroupStats);
  };

  // Debug: Log auth state
  useEffect(() => {
    console.log('[TrainingStats] Auth state:', { user: user?.uid, role, isAdmin: role === 'admin' });
  }, [user, role]);

  // Load training games from Backend API
  useEffect(() => {
    const loadData = async () => {
      try {
        console.log('[TrainingStats] Fetching from:', `${BACKEND_URL}/api/training/games`);
        const response = await fetch(`${BACKEND_URL}/api/training/games`);
        const data = await response.json();
        
        console.log('[TrainingStats] Response:', data);
        
        if (!data.ok || !data.games || data.games.length === 0) {
          console.log('[TrainingStats] No games found');
          setLoading(false);
          return;
        }
        
        const games = data.games;
        setTrainingGames(games);
        
        // Calculate stats
        const stats = {};
        const groupData = { duo: {}, trio: {}, quartett: {}, five: {} };
        
        games.forEach(game => {
          const winningTeam = game.winner === 'A' ? game.team_a_players : game.team_b_players;
          const losingTeam = game.winner === 'A' ? game.team_b_players : game.team_a_players;
          const winScore = game.winner === 'A' ? game.team_a_score : game.team_b_score;
          const loseScore = game.winner === 'A' ? game.team_b_score : game.team_a_score;
          
          // Individual stats
          winningTeam.forEach(playerId => {
            if (!stats[playerId]) stats[playerId] = { wins: 0, losses: 0, games: 0, pointsFor: 0, pointsAgainst: 0 };
            stats[playerId].wins++;
            stats[playerId].games++;
            stats[playerId].pointsFor += winScore;
            stats[playerId].pointsAgainst += loseScore;
          });
          
          losingTeam.forEach(playerId => {
            if (!stats[playerId]) stats[playerId] = { wins: 0, losses: 0, games: 0, pointsFor: 0, pointsAgainst: 0 };
            stats[playerId].losses++;
            stats[playerId].games++;
            stats[playerId].pointsFor += loseScore;
            stats[playerId].pointsAgainst += winScore;
          });
          
          // Group stats
          const trackGroups = (teamPlayers, isWin, scoreFor, scoreAgainst) => {
            [2, 3, 4, 5].forEach(k => {
              if (teamPlayers.length >= k) {
                const groupKey = k === 2 ? 'duo' : k === 3 ? 'trio' : k === 4 ? 'quartett' : 'five';
                getCombinations(teamPlayers, k).forEach(combo => {
                  const key = combo.sort().join('_');
                  if (!groupData[groupKey][key]) {
                    groupData[groupKey][key] = { players: combo, wins: 0, losses: 0, games: 0, pointsFor: 0, pointsAgainst: 0 };
                  }
                  groupData[groupKey][key].games++;
                  groupData[groupKey][key].pointsFor += scoreFor;
                  groupData[groupKey][key].pointsAgainst += scoreAgainst;
                  if (isWin) groupData[groupKey][key].wins++;
                  else groupData[groupKey][key].losses++;
                });
              }
            });
          };
          
          trackGroups(winningTeam, true, winScore, loseScore);
          trackGroups(losingTeam, false, loseScore, winScore);
        });
        
        // Calculate rates
        Object.keys(stats).forEach(id => {
          stats[id].winRate = stats[id].games > 0 ? (stats[id].wins / stats[id].games * 100).toFixed(0) : 0;
          stats[id].diff = stats[id].pointsFor - stats[id].pointsAgainst;
        });
        
        const processGroup = (data) => Object.values(data).map(g => ({
          ...g,
          winRate: g.games > 0 ? (g.wins / g.games * 100).toFixed(0) : 0,
          diff: g.pointsFor - g.pointsAgainst
        })).filter(g => g.games >= 2);
        
        const einzelArray = Object.entries(stats).map(([id, s]) => ({
          players: [id], ...s
        })).filter(p => p.games >= 1);
        
        setGroupStats({
          einzel: einzelArray,
          duo: processGroup(groupData.duo),
          trio: processGroup(groupData.trio),
          quartett: processGroup(groupData.quartett),
          five: processGroup(groupData.five)
        });
        
        console.log('[TrainingStats] Processed stats:', { einzel: einzelArray.length, duo: Object.keys(groupData.duo).length });
      } catch (err) {
        console.error('[TrainingStats] Error:', err);
        setError(err.message);
      }
      setLoading(false);
    };
    
    loadData();
  }, []);

  // Delete all training games (Admin only)
  const deleteAllGames = async () => {
    console.log('[TrainingStats] deleteAllGames called', { 
      user: user?.uid, 
      role, 
      gamesCount: trainingGames.length,
      isAdmin: role === 'admin'
    });
    
    if (!user) {
      alert('Bitte erst einloggen');
      return;
    }
    
    if (role !== 'admin') {
      alert('Nur Admins können löschen');
      return;
    }
    
    if (trainingGames.length === 0) {
      alert('Keine Spiele zum Löschen vorhanden');
      return;
    }
    
    const confirmDelete = window.confirm(`Wirklich alle ${trainingGames.length} Trainingsspiele löschen?`);
    if (!confirmDelete) {
      console.log('[TrainingStats] User cancelled deletion');
      return;
    }
    
    setDeleting(true);
    
    try {
      const apiUrl = `${BACKEND_URL}/api/training/games/delete_all`;
      console.log('[TrainingStats] Calling delete_all API:', apiUrl);
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ admin_id: user.uid })
      });
      
      console.log('[TrainingStats] Response status:', response.status);
      const result = await response.json();
      console.log('[TrainingStats] Delete all response:', result);
      
      if (result.ok) {
        setTrainingGames([]);
        setGroupStats({ einzel: [], duo: [], trio: [], quartett: [], five: [] });
        alert(`✅ ${result.deleted_count} Trainingsspiele gelöscht!`);
      } else {
        alert(`❌ Fehler: ${result.detail || 'Unbekannter Fehler'}`);
      }
    } catch (err) {
      console.error('[TrainingStats] Delete error:', err);
      alert('❌ Fehler beim Löschen: ' + err.message);
    }
    setDeleting(false);
  };

  // Get player name - handle guests with special format
  const getPlayerName = (playerId) => {
    // Check if it's a guest
    if (playerId && playerId.startsWith('guest_')) {
      // Guest format: guest_{eventId}_{index} or guest_manual_{timestamp}
      const parts = playerId.split('_');
      const index = parseInt(parts[parts.length - 1]) + 1 || 1;
      return `Gast #${index} (Gast)`;
    }
    const u = allUsers?.find(u => u.id === playerId);
    return u?.display_name || u?.name || playerId?.slice(0, 8) || '?';
  };

  // Check if player is a guest
  const isGuest = (playerId) => playerId && playerId.startsWith('guest_');

  const getShortName = (playerId) => {
    const name = getPlayerName(playerId);
    // For guests, return shorter format
    if (isGuest(playerId)) {
      const parts = playerId.split('_');
      const index = parseInt(parts[parts.length - 1]) + 1 || 1;
      return `G#${index}`;
    }
    return name.split(' ')[0];
  };

  const sortedData = useMemo(() => {
    const data = groupStats[groupView] || [];
    return [...data].sort((a, b) => {
      switch (sortBy) {
        case 'wins': return b.wins - a.wins;
        case 'losses': return b.losses - a.losses;
        case 'winRate': return parseFloat(b.winRate) - parseFloat(a.winRate);
        case 'pointsFor': return b.pointsFor - a.pointsFor;
        case 'pointsAgainst': return b.pointsAgainst - a.pointsAgainst;
        case 'diff': return b.diff - a.diff;
        default: return b.wins - a.wins;
      }
    });
  }, [groupStats, groupView, sortBy]);

  const viewLabels = { einzel: 'Einzel', duo: 'Duo', trio: 'Trio', quartett: 'Quartett', five: 'Five' };
  const sortLabels = { wins: 'Siege', losses: 'Ndlg.', winRate: '%', pointsFor: 'Pkt', pointsAgainst: 'Geg', diff: '+/-' };

  if (loading) return <div className="text-rose-200/50 text-sm p-2">Lade...</div>;
  
  if (error) return <div className="text-red-400 text-sm p-2">Fehler: {error}</div>;

  if (trainingGames.length === 0) {
    return (
      <div className="bg-[#1a0d11] border border-white/10 rounded-lg p-3">
        <p className="text-sm text-rose-200/50">Noch keine Trainingsspiele.</p>
        <p className="text-xs text-rose-200/30 mt-1">Tipp: Bei Training → Teams bilden → Spiele eintragen</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {/* Summary - COMPACT */}
      <div className="bg-[#1a0d11] border border-orange-500/20 rounded-lg p-2">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-bold text-orange-300 flex items-center gap-1">
            <Trophy className="w-4 h-4" /> Training
          </span>
          <div className="flex items-center gap-2">
            <span className="text-xs text-rose-200/50">{trainingGames.length} Spiele</span>
            {/* Admin: Delete all button */}
            {role === 'admin' && (
              <button 
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  console.log('[TrainingStats] Delete all button clicked');
                  deleteAllGames();
                }}
                disabled={deleting}
                className="text-xs text-red-400 hover:text-red-300 flex items-center gap-1 px-2 py-1 rounded bg-red-900/20 border border-red-500/30"
                title="Alle Trainingsspiele löschen"
                data-testid="delete-all-training-games-btn"
              >
                <Trash2 className="w-3 h-3" />
                {deleting ? 'Lösche...' : 'Alle löschen'}
              </button>
            )}
          </div>
        </div>
        
        {/* Filter Row - COMPACT */}
        <div className="flex gap-2">
          <select
            value={groupView}
            onChange={(e) => setGroupView(e.target.value)}
            className="flex-1 p-1.5 text-xs rounded bg-white/5 border border-white/10 text-white"
            data-testid="group-view-select"
          >
            {Object.entries(viewLabels).map(([key, label]) => (
              <option key={key} value={key} className="bg-[#1a0d11]">
                {label} ({groupStats[key]?.length || 0})
              </option>
            ))}
          </select>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="w-20 p-1.5 text-xs rounded bg-white/5 border border-white/10 text-white"
            data-testid="sort-by-select"
          >
            {Object.entries(sortLabels).map(([key, label]) => (
              <option key={key} value={key} className="bg-[#1a0d11]">{label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* ADMIN: Individual Games List - Delete single games */}
      {role === 'admin' && trainingGames.length > 0 && (
        <div className="bg-[#1a0d11] border border-red-500/30 rounded-lg p-2">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-red-300 flex items-center gap-1">
              <Trash2 className="w-3 h-3" /> Spiele verwalten ({trainingGames.length})
            </span>
            <button 
              onClick={toggleShowAllGames}
              className="text-[10px] text-white/50 hover:text-white"
            >
              {showAllGames ? '▲ Zuklappen' : '▼ Aufklappen'}
            </button>
          </div>
          
          {showAllGames && (
            <div className="overflow-x-auto max-h-60 overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-[#1a0d11]">
                  <tr className="border-b border-white/10 text-white/60">
                    <th className="text-left p-1 w-8">#</th>
                    <th className="text-center p-1 w-8 text-blue-300" title="Team Blau">B</th>
                    <th className="text-center p-1" title="Ergebnis">Erg.</th>
                    <th className="text-center p-1 w-8 text-red-300" title="Team Rot">R</th>
                    <th className="text-left p-1" title="Datum">Datum</th>
                    <th className="text-center p-1 w-8" title="Löschen">🗑️</th>
                  </tr>
                </thead>
                <tbody>
                  {trainingGames.map((game, idx) => (
                    <tr key={game.id} className="border-b border-white/5 hover:bg-white/5">
                      <td className="p-1 text-white/40">{idx + 1}</td>
                      <td className="p-1 text-center text-blue-300">{game.team_a_players?.length || 0}</td>
                      <td className="p-1 text-center">
                        <span className={`font-bold ${game.winner === 'A' ? 'text-green-400' : 'text-white'}`}>
                          {game.team_a_score}
                        </span>
                        <span className="text-white/30 mx-0.5">:</span>
                        <span className={`font-bold ${game.winner === 'B' ? 'text-green-400' : 'text-white'}`}>
                          {game.team_b_score}
                        </span>
                      </td>
                      <td className="p-1 text-center text-red-300">{game.team_b_players?.length || 0}</td>
                      <td className="p-1 text-white/40">
                        {game.created_at ? new Date(game.created_at).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' }) : '-'}
                      </td>
                      <td className="p-1 text-center">
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            console.log('[TrainingStats] Delete button clicked for game:', game.id);
                            deleteSingleGame(game.id);
                          }}
                          disabled={deletingGameId === game.id}
                          className="text-red-400 hover:text-red-300 hover:bg-red-900/30 p-1 rounded transition-colors"
                          title="Dieses Spiel löschen"
                          data-testid={`delete-game-${idx}`}
                        >
                          {deletingGameId === game.id ? '...' : <Trash2 className="w-3 h-3" />}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Stats Table - with column headers */}
      <div className="bg-[#1a0d11] border border-white/10 rounded-lg p-2">
        <div className="text-xs font-semibold text-white mb-2 flex items-center gap-1">
          {groupView === 'einzel' && <Medal className="w-3 h-3 text-yellow-400" />}
          {groupView !== 'einzel' && <Users className="w-3 h-3 text-blue-400" />}
          {viewLabels[groupView]} ({sortedData.length})
        </div>
        
        {sortedData.length === 0 ? (
          <p className="text-xs text-rose-200/50">Keine Daten (min. 2 Spiele für Gruppen)</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-white/10 text-white/60">
                  <th className="text-left p-1 w-6">#</th>
                  <th className="text-left p-1">Name</th>
                  <th className="text-center p-1 w-6 text-green-400" title="Siege">S</th>
                  <th className="text-center p-1 w-6 text-red-400" title="Niederlagen">N</th>
                  <th className="text-center p-1 w-10" title="Siegquote">%</th>
                  <th className="text-center p-1 w-8" title="Punktedifferenz">+/-</th>
                </tr>
              </thead>
              <tbody>
                {sortedData.slice(0, 15).map((item, idx) => (
                  <tr 
                    key={item.players.join('_')}
                    className={`border-b border-white/5 ${
                      idx === 0 ? 'bg-yellow-600/20' : idx === 1 ? 'bg-gray-500/20' : idx === 2 ? 'bg-orange-600/20' : ''
                    }`}
                  >
                    <td className="p-1">
                      <span className={`w-4 h-4 inline-flex items-center justify-center rounded-full text-[10px] font-bold ${
                        idx === 0 ? 'bg-yellow-500 text-black' : idx === 1 ? 'bg-gray-400 text-black' : idx === 2 ? 'bg-orange-600 text-white' : 'bg-white/10 text-white/50'
                      }`}>{idx + 1}</span>
                    </td>
                    <td className="p-1 truncate max-w-[120px]">
                      {item.players.map(p => {
                        const name = groupView === 'einzel' ? getPlayerName(p) : getShortName(p);
                        return isGuest(p) ? <span key={p} className="text-blue-300 italic">{name}</span> : name;
                      }).reduce((prev, curr, i) => i === 0 ? [curr] : [...prev, ' & ', curr], [])}
                    </td>
                    <td className="p-1 text-center text-green-400 font-bold">{item.wins}</td>
                    <td className="p-1 text-center text-red-400 font-bold">{item.losses}</td>
                    <td className="p-1 text-center">
                      <span className={`px-1 rounded ${
                        parseFloat(item.winRate) >= 60 ? 'bg-green-600/30 text-green-300' : 
                        parseFloat(item.winRate) >= 40 ? 'bg-yellow-600/30 text-yellow-300' : 
                        'bg-red-600/30 text-red-300'
                      }`}>
                        {item.winRate}%
                      </span>
                    </td>
                    <td className={`p-1 text-center font-bold ${
                      item.diff > 0 ? 'text-green-400' : item.diff < 0 ? 'text-red-400' : 'text-white/50'
                    }`}>
                      {item.diff > 0 ? '+' : ''}{item.diff}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {sortedData.length > 15 && (
              <p className="text-[10px] text-center text-rose-200/30 mt-1">+{sortedData.length - 15} weitere</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
